import { SerializedStyles, css } from '@emotion/react';

import { Fragment, ReactNode, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';

import { Spacing } from 'chatie-design-system';
import sum from 'lodash/sum';
import { v4 as uuidv4 } from 'uuid';

import {
  IAnyPagination,
  IChatPagination,
  IKeyPagination,
  IPagination,
} from '../../model';
import { CSSProps } from '../../utils/misc';
import { UsePaginationQueryResult } from '../../utils/query';
import List, { ListProps } from '../layout/List';
import Loading from './Loading';

type Props<T> = {
  query: UsePaginationQueryResult<T>;
  children?: ReactNode;
};

export const Pagination = <
  T extends
    | IKeyPagination<unknown>
    | IPagination<unknown>
    | IChatPagination<unknown>
>({
  query,
  children,
}: Props<T>) => {
  const { ref, inView } = useInView();

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;

  useEffect(() => {
    if (!isFetchingNextPage && hasNextPage && inView) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, inView, fetchNextPage]);

  return hasNextPage ? (
    <div
      ref={ref}
      onClick={isFetchingNextPage ? undefined : () => fetchNextPage()}
    >
      {children}
    </div>
  ) : null;
};

export default Pagination;

type IteratorProps<T> = {
  name: string;
  query: UsePaginationQueryResult<IAnyPagination<T>>;
  ad?: ReactNode;
  identifier?: string;
  next?: boolean;
  direction?: 'vertical' | 'horizontal';
  children: (item: T, index: number, pageIndex: number) => ReactNode;
  css?: SerializedStyles;
  prevComponent?: ReactNode;
};

const horizontalCSS = css`
  white-space: nowrap;
  overflow: auto;

  li {
    display: inline-block;
    vertical-align: middle;
  }

  > * + * {
    margin-left: ${Spacing.xsmall}px;
  }
`;

export const PaginationIterator = <T,>({
  className,
  name,
  query,
  ad,
  border,
  margin,
  identifier = 'id',
  direction = 'vertical',
  next = true,
  prevComponent,
  css,
  children,
}: IteratorProps<T> & Pick<ListProps, 'margin' | 'border'> & CSSProps) => {
  const { isError, data } = query;

  if (isError) {
    return null;
  }

  return (
    <>
      <List
        className={className}
        css={css ? css : direction === 'horizontal' ? horizontalCSS : undefined}
        border={border}
        margin={margin}
      >
        {ad && <li>{ad}</li>}
        {prevComponent && prevComponent}
        {data?.pages?.map((page, i) => {
          const key =
            'page' in page ? page.page : page.lastEvaluatedKey ?? page.next;
          const prevItemLength = sum(
            data?.pages.map((p) => p.items.length).slice(0, i)
          );

          return (
            <Fragment key={`${name}-${key}`}>
              {page.items.map((item, j) => {
                const rendered = children(item, prevItemLength + j, i);

                if (rendered) {
                  return (
                    <li
                      key={`${name}-${key}-${
                        item[identifier as keyof typeof item] ?? uuidv4()
                      }`}
                    >
                      {rendered}
                    </li>
                  );
                }

                return null;
              })}
            </Fragment>
          );
        }) ?? <Loading push />}
        {direction === 'horizontal' && next && query.hasNextPage && (
          <li>
            <Pagination query={query}>
              <Loading size={6} />
            </Pagination>
          </li>
        )}
      </List>
      {direction === 'vertical' && next && query.hasNextPage && (
        <Pagination query={query}>
          <Loading />
        </Pagination>
      )}
    </>
  );
};
